import type { RouterInputs } from '@client/trpc/client';
import { HARD_OFFSET_LIMIT } from '@shared/constants/system/general';
import { z } from 'zod';

type FaqWhere = NonNullable<RouterInputs['faq']['listFaqs']['where']>;

/** Most categories a view may select; the category filter stops at the same count. */
export const FAQ_MAX_CATEGORIES = 20;

/** Longest search text the URL keeps; the search input stops at the same length. */
export const FAQ_SEARCH_MAX_LENGTH = 200;

/**
 * Longest category the URL keeps. FAQ owners can save longer ones, which the category filter
 * shows but does not let a user choose: one in the URL would drop the whole selection.
 */
export const FAQ_CATEGORY_MAX_LENGTH = 100;

/**
 * The FAQ page view kept in the URL so it survives refresh, Back and sharing. Every field is
 * optional and falls back to "absent" when a link carries a bad value.
 */
export const FaqSearchSchema = z.object({
    /** Search text, applied to question titles and answers. */
    q: z.string().trim().max(FAQ_SEARCH_MAX_LENGTH).optional().catch(undefined),
    /** Selected categories; a question matches when it has any of them. */
    category: z.array(z.string().max(FAQ_CATEGORY_MAX_LENGTH)).max(FAQ_MAX_CATEGORIES).optional().catch(undefined),
    /**
     * The id of the open question. The app writes it JSON-quoted (`?open=%2214%22`); a hand-written
     * `?open=14` reaches the schema as a number, so that form is accepted too.
     */
    open: z
        .union([z.string(), z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)])
        .transform((value) => String(value))
        .pipe(z.string().regex(/^\d+$/).max(20))
        .optional()
        .catch(undefined),
});

export type FaqSearch = z.infer<typeof FaqSearchSchema>;

/** The category filter's options: every category, those the URL cannot keep disabled. */
export function faqCategoryOptions(categories: readonly string[]) {
    return categories.map((category) => ({
        label: category,
        value: category,
        disabled: category.length > FAQ_CATEGORY_MAX_LENGTH,
    }));
}

/** Builds the `listFaqs` filter for a search text and category selection. */
export function buildFaqWhere({ q, category }: Pick<FaqSearch, 'q' | 'category'>): FaqWhere | undefined {
    const and: FaqWhere[] = [];

    if (category && category.length > 0) {
        and.push({ OR: category.map((value) => ({ categories: { has: value } })) });
    }

    const term = q?.trim();
    if (term) {
        and.push({
            OR: [
                { title: { contains: term, mode: 'insensitive' } },
                { content: { contains: term, mode: 'insensitive' } },
            ],
        });
    }

    return and.length > 0 ? { AND: and } : undefined;
}

/** A stable key for the filter a search text and category selection apply. */
export function faqFilterKey(view: Pick<FaqSearch, 'q' | 'category'>): string {
    return JSON.stringify(buildFaqWhere(view) ?? null);
}

/**
 * The loaded pages as one list, each question once. Offset pages shift when a question is
 * published or unpublished between requests, so a later page can repeat a question already shown.
 */
export function flattenFaqPages<Row extends { id: bigint | number | string }>(
    pages: readonly { rows: readonly Row[] }[],
): Row[] {
    const byId = new Map<string, Row>();
    for (const page of pages) {
        for (const row of page.rows) {
            const id = String(row.id);
            if (!byId.has(id)) byId.set(id, row);
        }
    }
    return [...byId.values()];
}

/**
 * Whether the loaded pages count the same list. A question published or unpublished between two
 * page requests changes the total and shifts every offset after it: after an unpublish, the
 * question that slides back onto an earlier page is in no page requested.
 */
export function faqPagesAgree(pages: readonly { total: number }[]): boolean {
    const [first] = pages;
    return pages.every((page) => page.total === first?.total);
}

/**
 * The offset of the next `listFaqs` page, or `undefined` when every question is loaded. Loading
 * also stops once the next offset would pass the server's `HARD_OFFSET_LIMIT`, which refuses it.
 */
export function nextFaqPageOffset(pages: readonly { rows: readonly unknown[]; total: number }[]): number | undefined {
    const lastPage = pages.at(-1);
    const loaded = pages.reduce((sum, page) => sum + page.rows.length, 0);
    if (!lastPage || lastPage.rows.length === 0 || loaded >= lastPage.total || loaded > HARD_OFFSET_LIMIT) {
        return undefined;
    }
    return loaded;
}
